import {
  RolePermissionsByEntity,
  PermissionValidationResult,
  PermissionValidationError,
  PermissionValidationRules,
} from "../types/permissions";

// Default validation rules
export const DEFAULT_VALIDATION_RULES: PermissionValidationRules = {
  minPermissions: 1,
  allowDuplicates: false,
  requiredEntities: [],
};

/**
 * Validates role permissions based on the provided rules
 */
export const validatePermissions = (
  permissions: RolePermissionsByEntity,
  rules: PermissionValidationRules = DEFAULT_VALIDATION_RULES
): PermissionValidationResult => {
  const errors: PermissionValidationError[] = [];
  const warnings: string[] = [];

  // Count selected permissions
  const selectedPermissions = getSelectedPermissionsFromState(permissions);
  const selectedCount = selectedPermissions.length;

  // Rule 1: Minimum permissions check
  if (selectedCount < rules.minPermissions) {
    errors.push({
      field: "permissions",
      message: `At least ${rules.minPermissions} permission${
        rules.minPermissions > 1 ? "s" : ""
      } must be selected. Currently ${selectedCount} selected.`,
    });
  }

  // Rule 2: Check for duplicates (shouldn't happen with our UI, but good to validate)
  if (!rules.allowDuplicates) {
    const duplicates = findDuplicatePermissions(selectedPermissions);
    if (duplicates.length > 0) {
      errors.push({
        field: "permissions",
        message: `Duplicate permissions found: ${duplicates.join(", ")}`,
      });
    }
  }

  // Rule 3: Required entities check
  if (rules.requiredEntities && rules.requiredEntities.length > 0) {
    const missingRequiredEntities = rules.requiredEntities.filter(
      (entity) => !hasEntityPermissions(permissions, entity)
    );

    if (missingRequiredEntities.length > 0) {
      errors.push({
        field: "permissions",
        message: `Required entities must have at least one permission: ${missingRequiredEntities.join(
          ", "
        )}`,
      });
    }
  }

  // Warning: Check for potentially dangerous permissions
  const dangerousPermissions = selectedPermissions.filter((perm) =>
    perm.includes("delete") || perm.includes("casbin_policies")
  );

  if (dangerousPermissions.length > 0) {
    warnings.push(
      `This role includes potentially sensitive permissions: ${dangerousPermissions.join(
        ", "
      )}`
    );
  }

  // Warning: Check for very broad permissions
  if (selectedCount > 50) {
    warnings.push(
      `This role has a large number of permissions (${selectedCount}). Consider if all are necessary.`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Extracts selected permissions from the permission state
 */
export const getSelectedPermissionsFromState = (
  permissions: RolePermissionsByEntity
): string[] => {
  const selected: string[] = [];

  Object.entries(permissions).forEach(([entity, actions]) => {
    Object.entries(actions).forEach(([action, isSelected]) => {
      if (isSelected) {
        selected.push(`${entity}:${action}`);
      }
    });
  });

  return selected;
};

/**
 * Finds duplicate permissions in the list
 */
export const findDuplicatePermissions = (permissions: string[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  permissions.forEach((permission) => {
    if (seen.has(permission)) {
      duplicates.add(permission);
    } else {
      seen.add(permission);
    }
  });

  return Array.from(duplicates);
};

/**
 * Checks if an entity has any permissions selected
 */
export const hasEntityPermissions = (
  permissions: RolePermissionsByEntity,
  entity: string
): boolean => {
  const entityPermissions = permissions[entity];
  if (!entityPermissions) return false;

  return Object.values(entityPermissions).some((isSelected) => isSelected);
};

/**
 * Validates individual permission format
 */
export const validatePermissionFormat = (permission: string): boolean => {
  const parts = permission.split(":");
  return parts.length === 2 && parts[0].length > 0 && parts[1].length > 0;
};

/**
 * Sanitizes permission data by removing invalid entries
 */
export const sanitizePermissions = (
  permissions: RolePermissionsByEntity
): RolePermissionsByEntity => {
  const sanitized: RolePermissionsByEntity = {};

  Object.entries(permissions).forEach(([entity, actions]) => {
    if (entity && typeof entity === "string") {
      const sanitizedActions: { [key: string]: boolean } = {};

      Object.entries(actions).forEach(([action, isSelected]) => {
        if (
          action &&
          typeof action === "string" &&
          typeof isSelected === "boolean"
        ) {
          sanitizedActions[action] = isSelected;
        }
      });

      if (Object.keys(sanitizedActions).length > 0) {
        sanitized[entity] = sanitizedActions;
      }
    }
  });

  return sanitized;
};

/**
 * Compares two permission states to detect changes
 */
export const hasPermissionChanges = (
  original: RolePermissionsByEntity,
  current: RolePermissionsByEntity
): boolean => {
  const originalSelected = getSelectedPermissionsFromState(original);
  const currentSelected = getSelectedPermissionsFromState(current);

  if (originalSelected.length !== currentSelected.length) return true;

  const originalSet = new Set(originalSelected);
  return currentSelected.some((perm) => !originalSet.has(perm));
};

/**
 * Creates a summary of permission changes
 */
export const getPermissionChangesSummary = (
  original: RolePermissionsByEntity,
  current: RolePermissionsByEntity
): {
  added: string[];
  removed: string[];
  total: number;
} => {
  const originalSelected = new Set(getSelectedPermissionsFromState(original));
  const currentSelected = new Set(getSelectedPermissionsFromState(current));

  const added = Array.from(currentSelected).filter(
    (perm) => !originalSelected.has(perm)
  );
  const removed = Array.from(originalSelected).filter(
    (perm) => !currentSelected.has(perm)
  );

  return {
    added,
    removed,
    total: added.length + removed.length,
  };
};
